import { useState, type ChangeEvent } from "react";
import { Upload, X } from "lucide-react";

interface FileUploadFieldProps {
  label: string;
  accept: string; // e.g. "application/pdf,image/*"
  maxSizeMB: number;
  onFileSelected: (filename: string | null) => void;
}

/**
 * Type/size validation is real and useful on its own — pure client-side
 * checks with no network dependency. The file itself is never actually
 * uploaded anywhere: this project has no cloud storage, matching the
 * established precedent from TestimonialSubmissionForm.tsx's cosmetic file
 * input (ZP-40). Only the filename is kept and handed to the parent form.
 */
/** Checks a file against an `accept`-style pattern list (MIME types, "image/*" wildcards, or ".ext" extensions) — the `accept` attribute on the input itself is only a picker hint, not enforcement (an "All Files" picker or drag-and-drop bypasses it entirely). */
function matchesAccept(file: File, accept: string): boolean {
  const patterns = accept.split(",").map((p) => p.trim().toLowerCase());
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();
  return patterns.some((pattern) => {
    if (pattern.startsWith(".")) return fileName.endsWith(pattern);
    if (pattern.endsWith("/*")) return fileType.startsWith(pattern.slice(0, -1));
    return fileType === pattern;
  });
}

export default function FileUploadField({ label, accept, maxSizeMB, onFileSelected }: FileUploadFieldProps) {
  const [fileName, setFileName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setFileName(null);
      setError(null);
      onFileSelected(null);
      return;
    }

    if (!matchesAccept(file, accept)) {
      setError(`${file.name} isn't an accepted file type.`);
      setFileName(null);
      onFileSelected(null);
      e.target.value = "";
      return;
    }

    const maxBytes = maxSizeMB * 1024 * 1024;
    if (file.size > maxBytes) {
      setError(`${file.name} is too large — max ${maxSizeMB}MB.`);
      setFileName(null);
      onFileSelected(null);
      e.target.value = "";
      return;
    }

    setError(null);
    setFileName(file.name);
    onFileSelected(file.name);
  };

  const clear = () => {
    setFileName(null);
    setError(null);
    onFileSelected(null);
  };

  return (
    <div className="text-sm">
      <span className="font-medium text-ocean-900 dark:text-foam-100">{label}</span>
      <div className="mt-1.5 flex items-center gap-3">
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-ocean-900/5 px-4 py-2 text-xs font-semibold text-ocean-800 hover:bg-ocean-900/10 dark:bg-white/10 dark:text-foam-100 dark:hover:bg-white/15">
          <Upload className="h-3.5 w-3.5" />
          Choose file
          <input type="file" accept={accept} className="hidden" onChange={handleChange} />
        </label>
        {fileName && (
          <span className="flex items-center gap-1.5 truncate text-xs text-ocean-800/60 dark:text-foam-100/60">
            {fileName}
            <button type="button" onClick={clear} aria-label={`Remove ${fileName}`} className="text-ocean-800/40 hover:text-coral-500">
              <X className="h-3.5 w-3.5" />
            </button>
          </span>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-coral-500">{error}</p>}
    </div>
  );
}
